import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { usePlatformConfig } from '../../hooks/usePlatformConfig';

const {
  FiX, FiCalendar, FiMapPin, FiVideo, FiRadio, FiSave,
  FiCheck, FiInfo, FiCopy, FiGlobe, FiShield, FiLink,
  FiChevronDown, FiChevronUp, FiAlertTriangle,
} = FiIcons;

const EVENT_TYPES = [
  { id: 'Livestream',    label: 'Live Ceremony',   emoji: '🎥', desc: 'Stream the event live to attendees' },
  { id: 'Webinar',       label: 'Webinar / Meeting',emoji: '💻', desc: 'Interactive video meeting session'   },
  { id: 'In-Person',    label: 'In-Person Service', emoji: '🏛️', desc: 'Physical venue attendance'           },
  { id: 'Hybrid',        label: 'Hybrid Event',     emoji: '🌐', desc: 'Both physical and online presence'   },
];

const PlatformBadge = ({ emoji, label, isOverride, type }) => (
  <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest border ${isOverride ? 'bg-green-50 border-green-200 text-green-700' : 'bg-blue-50 border-blue-200 text-blue-700'}`}>
    <span>{emoji}</span>
    {label}
    <span className={`text-[8px] px-1.5 py-0.5 rounded-full ${isOverride ? 'bg-green-200 text-green-800' : 'bg-blue-200 text-blue-800'}`}>
      {isOverride ? 'Memorial Override' : 'Global Default'}
    </span>
  </div>
);

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const handle = () => {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={handle} type="button"
      className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest border transition-all ${copied ? 'bg-green-500 border-green-500 text-white' : 'bg-white border-brand-dark text-text-muted hover:border-accent-gold'}`}>
      <SafeIcon icon={copied ? FiCheck : FiCopy} className="w-2.5 h-2.5" />
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
};

const InfoRow = ({ label, value, mono = false, copyable = false }) => (
  <div className="flex items-start justify-between gap-4 py-2.5 border-b border-brand-dark/30 last:border-0">
    <span className="text-[9px] font-black uppercase tracking-widest text-text-muted flex-shrink-0 mt-0.5">{label}</span>
    <div className="flex items-center gap-2 min-w-0">
      <span className={`text-[11px] text-accent-navy text-right break-all ${mono ? 'font-mono' : 'font-bold'}`}>
        {value || <span className="text-text-muted italic">Not configured</span>}
      </span>
      {copyable && value && <CopyButton text={value} />}
    </div>
  </div>
);

const CreateEventModal = ({ isOpen, onClose, memorial, onEventCreated }) => {
  const config = usePlatformConfig(memorial?.id);

  const [title, setTitle]       = useState('');
  const [type, setType]         = useState('Livestream');
  const [date, setDate]         = useState('');
  const [time, setTime]         = useState('');
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');
  const [showPlatformDetails, setShowPlatformDetails] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [saved, setSaved]       = useState(false);

  const needsPlatform = ['Livestream', 'Webinar', 'Hybrid'].includes(type);
  const activeConfig  = type === 'Webinar' ? config.webinar : config.streaming;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title || !date || !time) return;
    setSaving(true);

    setTimeout(() => {
      const newEvent = {
        id: `evt-${Date.now()}`,
        title,
        type,
        date: new Date(`${date}T${time}`).toISOString(),
        location: location || (needsPlatform ? activeConfig.label : 'TBD'),
        description,
        platform: needsPlatform ? activeConfig.platformId : null,
        platformLabel: needsPlatform ? activeConfig.label : null,
      };

      if (onEventCreated) onEventCreated(newEvent);
      setSaving(false);
      setSaved(true);
      setTimeout(() => {
        setSaved(false);
        onClose();
        // Reset
        setTitle(''); setType('Livestream'); setDate('');
        setTime(''); setLocation(''); setDescription('');
      }, 2000);
    }, 1200);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-accent-navy/60 backdrop-blur-md overflow-y-auto">
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 20 }}
        className="bg-white rounded-[40px] shadow-2xl w-full max-w-2xl border border-brand-dark my-4"
      >
        {/* Header */}
        <div className="bg-accent-navy text-white rounded-t-[40px] p-8 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-accent-gold/20 rounded-2xl flex items-center justify-center">
              <SafeIcon icon={FiCalendar} className="w-6 h-6 text-accent-gold" />
            </div>
            <div>
              <h3 className="font-serif text-2xl">Schedule New Event</h3>
              <p className="text-white/50 text-[10px] font-black uppercase tracking-widest">
                for {memorial?.name || 'this memorial'}
              </p>
            </div>
          </div>
          <button onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-full transition-colors text-white/60 hover:text-white">
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>

        <AnimatePresence mode="wait">
          {saved ? (
            <motion.div key="success"
              initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
              className="p-16 text-center">
              <div className="w-20 h-20 bg-green-500 text-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl">
                <SafeIcon icon={FiCheck} className="w-10 h-10" />
              </div>
              <h4 className="text-3xl font-serif text-accent-navy mb-3">Event Scheduled</h4>
              <p className="text-text-muted font-light">Your event has been added to this memorial.</p>
            </motion.div>
          ) : (
            <motion.form key="form" onSubmit={handleSubmit} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="p-8 space-y-7">

                {/* Event type selector */}
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block mb-3">Event Type</label>
                  <div className="grid grid-cols-2 gap-3">
                    {EVENT_TYPES.map(et => (
                      <button key={et.id} type="button" onClick={() => setType(et.id)}
                        className={`p-4 rounded-2xl border-2 text-left transition-all ${type === et.id ? 'border-accent-navy bg-accent-navy/5' : 'border-brand-dark hover:border-accent-gold/40'}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-lg">{et.emoji}</span>
                          <p className={`text-xs font-black ${type === et.id ? 'text-accent-navy' : 'text-text-muted'}`}>{et.label}</p>
                        </div>
                        <p className="text-[10px] text-text-muted leading-tight">{et.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Platform resolution banner */}
                {needsPlatform && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                    className="overflow-hidden">
                    <div className="bg-brand-light rounded-2xl border border-brand-dark p-4 space-y-3">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <SafeIcon icon={type === 'Webinar' ? FiVideo : FiRadio}
                            className="w-4 h-4 text-accent-navy flex-shrink-0" />
                          <p className="text-xs font-black text-accent-navy">
                            {type === 'Webinar' ? 'Meeting Platform' : 'Streaming Platform'}
                          </p>
                        </div>
                        <PlatformBadge
                          emoji={activeConfig.emoji}
                          label={activeConfig.label}
                          isOverride={activeConfig.isOverride}
                        />
                      </div>

                      <p className="text-[10px] text-text-muted leading-relaxed">
                        {activeConfig.isOverride
                          ? `Using this memorial's custom platform override. Credentials were set in the Customization → Platform Preferences tab.`
                          : `Using the site-wide default platform. Memorial owners can override this in Customization → Platform Preferences.`}
                      </p>

                      <button type="button" onClick={() => setShowPlatformDetails(v => !v)}
                        className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-accent-gold hover:text-accent-navy transition-colors">
                        <SafeIcon icon={showPlatformDetails ? FiChevronUp : FiChevronDown} className="w-3 h-3" />
                        {showPlatformDetails ? 'Hide' : 'View'} platform details
                      </button>

                      <AnimatePresence>
                        {showPlatformDetails && (
                          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                            <div className="pt-3 border-t border-brand-dark space-y-0.5">
                              {type === 'Webinar' ? (
                                <>
                                  <InfoRow label="Platform"   value={activeConfig.label} />
                                  <InfoRow label="Account ID" value={activeConfig.keys?.accountId} mono />
                                  <InfoRow label="Client ID"  value={activeConfig.keys?.clientId}  mono />
                                  {!activeConfig.keys?.apiKey && !activeConfig.keys?.accountId && (
                                    <div className="flex items-center gap-2 mt-2 text-amber-600 bg-amber-50 border border-amber-200 rounded-xl p-3">
                                      <SafeIcon icon={FiAlertTriangle} className="w-3.5 h-3.5 flex-shrink-0" />
                                      <p className="text-[10px]">No credentials configured for this platform yet. Add them in {activeConfig.isOverride ? 'Customization → Platform Preferences' : 'Creator Admin → Webinar Integrations'}.</p>
                                    </div>
                                  )}
                                </>
                              ) : (
                                <>
                                  <InfoRow label="Platform"   value={activeConfig.label} />
                                  <InfoRow label="RTMP Server" value={activeConfig.rtmpBase} mono copyable />
                                  <InfoRow label="Full Ingest URL" value={activeConfig.rtmpFull} mono copyable />
                                  {!activeConfig.keys?.streamKey && (
                                    <div className="flex items-center gap-2 mt-2 text-amber-600 bg-amber-50 border border-amber-200 rounded-xl p-3">
                                      <SafeIcon icon={FiAlertTriangle} className="w-3.5 h-3.5 flex-shrink-0" />
                                      <p className="text-[10px]">Stream key not set. Configure it in {activeConfig.isOverride ? 'Customization → Platform Preferences' : 'Creator Admin → Streaming Integrations'}.</p>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </motion.div>
                )}

                {/* Event details */}
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block mb-2">Event Title *</label>
                  <input required value={title} onChange={e => setTitle(e.target.value)}
                    placeholder="e.g. Celebration of Life Service"
                    className="w-full px-5 py-3.5 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold transition-all text-sm font-medium" />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block mb-2">Date *</label>
                    <input required type="date" value={date} onChange={e => setDate(e.target.value)}
                      className="w-full px-5 py-3.5 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold transition-all text-sm font-medium" />
                  </div>
                  <div>
                    <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block mb-2">Time *</label>
                    <input required type="time" value={time} onChange={e => setTime(e.target.value)}
                      className="w-full px-5 py-3.5 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold transition-all text-sm font-medium" />
                  </div>
                </div>

                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block mb-2">
                    {needsPlatform ? 'Additional Location / Venue (Optional)' : 'Location *'}
                  </label>
                  <div className="relative">
                    <SafeIcon icon={FiMapPin} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted w-4 h-4" />
                    <input value={location} onChange={e => setLocation(e.target.value)}
                      placeholder={needsPlatform ? 'Physical venue address (if hybrid)' : 'Venue name or address'}
                      className="w-full pl-11 pr-5 py-3.5 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold transition-all text-sm font-medium" />
                  </div>
                </div>

                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block mb-2">Description</label>
                  <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3}
                    placeholder="Describe the event for attendees…"
                    className="w-full px-5 py-3.5 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold transition-all text-sm font-light resize-none" />
                </div>
              </div>

              {/* Footer */}
              <div className="px-8 pb-8 flex items-center justify-between gap-4">
                <div className="flex items-center gap-2 text-[10px] text-text-muted">
                  <SafeIcon icon={FiShield} className="w-3.5 h-3.5 text-accent-gold" />
                  Platform credentials are never exposed to attendees
                </div>
                <div className="flex gap-3">
                  <button type="button" onClick={onClose}
                    className="px-6 py-3 rounded-2xl border-2 border-brand-dark text-[11px] font-black uppercase tracking-widest text-text-muted hover:border-accent-gold transition-all">
                    Cancel
                  </button>
                  <button type="submit" disabled={saving}
                    className="flex items-center gap-2 px-8 py-3 bg-accent-navy text-white rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-accent-navy/90 transition-all shadow-lg active:scale-[0.98] disabled:opacity-60">
                    {saving
                      ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      : <SafeIcon icon={FiSave} className="w-4 h-4" />}
                    {saving ? 'Scheduling…' : 'Schedule Event'}
                  </button>
                </div>
              </div>
            </motion.form>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default CreateEventModal;